// Vendored from orca-front/lib/analytics/portable/ (PR #503). Promote to @kromatic/ab npm package once 3+ sites use this.
/**
 * Portable GA4 cookie parser.
 *
 * Reads `_ga` and `_ga_*` cookies via `document.cookie`. No framework or
 * Node imports — safe to vendor into any browser-runtime site.
 *
 * The parsed `ga_client_id` is the canonical per-browser anonymous id GA4
 * uses for client identity. We use it as the experiment-bucketing seed so
 * the same browser sees the same variant across visits without storing any
 * additional identifier.
 */

import type { GaIds } from './types';

function safeDecode(value: string): string {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

function parseCookieString(cookieString: string): Record<string, string> {
    return cookieString
        .split(';')
        .map(cookie => cookie.trim())
        .filter(Boolean)
        .reduce<Record<string, string>>((acc, cookie) => {
            const separatorIndex = cookie.indexOf('=');

            if (separatorIndex === -1) {
                return acc;
            }

            const key = cookie.slice(0, separatorIndex).trim();
            const value = cookie.slice(separatorIndex + 1).trim();

            if (!key) {
                return acc;
            }

            acc[key] = safeDecode(value);
            return acc;
        }, {});
}

/**
 * Extracts the `<random>.<timestamp>` client id from a `_ga` cookie value.
 */
export function parseGaClientId(rawValue?: string): string | undefined {
    if (!rawValue) {
        return undefined;
    }

    const value = rawValue.trim();

    if (!value) {
        return undefined;
    }

    const parts = value.split('.');

    if (parts.length >= 4 && parts[2] && parts[3]) {
        return `${parts[2]}.${parts[3]}`;
    }

    const dottedPair = value.match(/\d+\.\d+/)?.[0];
    return dottedPair ?? value;
}

/**
 * Extracts the GA4 session id from a `_ga_<MEASUREMENT_ID>` cookie.
 */
export function parseGaSessionId(rawValue?: string): string | undefined {
    if (!rawValue) {
        return undefined;
    }

    const value = rawValue.trim();

    if (!value) {
        return undefined;
    }

    const firstNumericChunk = value.split('.').find(chunk => /^\d{6,}$/.test(chunk));

    return firstNumericChunk;
}

/**
 * Parses GA4 ids from an arbitrary cookie string. Useful for tests and SSR
 * code paths where you have the raw `Cookie` header.
 */
export function getGaIdsFromCookieString(cookieString: string): GaIds {
    const cookies = parseCookieString(cookieString);
    const gaCookieValue = cookies._ga;
    const gaSessionCookieValue = Object.entries(cookies).find(([key]) => key.startsWith('_ga_') && key !== '_ga')?.[1];

    const ga_client_id = parseGaClientId(gaCookieValue);
    const ga_session_id = parseGaSessionId(gaSessionCookieValue);

    return {
        ...(ga_client_id ? { ga_client_id } : {}),
        ...(ga_session_id ? { ga_session_id } : {}),
    };
}

/**
 * Parses GA4 ids from `document.cookie`. Returns an empty object when called
 * outside a browser (SSR / static-render path).
 */
export function getGaIdsFromBrowserCookies(): GaIds {
    if (typeof document === 'undefined') {
        return {};
    }

    return getGaIdsFromCookieString(document.cookie || '');
}

/**
 * Convenience helper: returns the GA4 client id (or `undefined`) to use as
 * the experiment-bucketing seed. Equivalent to
 * `getGaIdsFromBrowserCookies().ga_client_id`.
 */
export function getAssignmentSeed(): string | undefined {
    return getGaIdsFromBrowserCookies().ga_client_id;
}
